import type { PostgrestError } from '@supabase/supabase-js';

// ============================================
// UNIFIED ERROR HANDLING FOR GRAPH OPERATIONS
// ============================================
//
// This module provides standardized error handling for all graph operations.
// Based on Principal Engineer Review recommendations (section #7).
//
// ARCHITECTURE:
// - GraphError: Base error class with typed error codes
// - Specific error classes: GraphNotFoundError, GraphPermissionError, etc.
// - Helper functions: createPermissionError, handleSupabaseError, etc.
//
// BENEFITS:
// - Consistent error messages across all graph operations
// - Type-safe error codes for client-side error handling
// - Detailed context for debugging
// - Easy to test and mock
//
// ============================================

/**
 * Error codes for graph operations
 */
export enum GraphErrorCode {
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  EDGE_VALIDATION_ERROR = 'EDGE_VALIDATION_ERROR',
  DATABASE_ERROR = 'DATABASE_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Base error class for all graph operations
 */
export class GraphError extends Error {
  constructor(
    message: string,
    public code: GraphErrorCode,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'GraphError';

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, GraphError);
    }
  }

  /**
   * Serialize error to JSON for logging and error responses
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      details: this.details,
    };
  }
}

/**
 * Error thrown when a graph entity is not found
 */
export class GraphNotFoundError extends GraphError {
  constructor(entityType: string, identifier: string, details?: Record<string, unknown>) {
    super(`${entityType} not found: ${identifier}`, GraphErrorCode.NOT_FOUND, { entityType, identifier, ...details });
    this.name = 'GraphNotFoundError';
  }
}

/**
 * Error thrown when permission is denied
 */
export class GraphPermissionError extends GraphError {
  constructor(operation: string, entityType: string, type: string, details?: Record<string, unknown>) {
    super(`Permission denied: ${entityType} type '${type}' is not enabled for this tenant`, GraphErrorCode.PERMISSION_DENIED, { operation, entityType, type, ...details });
    this.name = 'GraphPermissionError';
  }
}

/**
 * Error thrown when validation fails
 */
export class GraphValidationError extends GraphError {
  constructor(message: string, errors: unknown[], details?: Record<string, unknown>) {
    super(message, GraphErrorCode.VALIDATION_ERROR, { validationErrors: errors, ...details });
    this.name = 'GraphValidationError';
  }
}

/**
 * Error thrown when edge type validation fails
 */
export class GraphEdgeValidationError extends GraphError {
  constructor(edgeType: string, nodeType: string, nodeRole: 'source' | 'target', details?: Record<string, unknown>) {
    super(`Invalid ${nodeRole} node type '${nodeType}' for edge type '${edgeType}'`, GraphErrorCode.EDGE_VALIDATION_ERROR, { edgeType, nodeType, nodeRole, ...details });
    this.name = 'GraphEdgeValidationError';
  }
}

/**
 * Error thrown when duplicate entity is created
 */
export class GraphDuplicateError extends GraphError {
  constructor(entityType: string, identifier: string, details?: Record<string, unknown>) {
    // Special formatting for edges
    const message = entityType === 'edge' ? `Edge of type '${identifier}' already exists between nodes` : `${entityType} already exists: ${identifier}`;

    super(message, GraphErrorCode.DUPLICATE, { entityType, identifier, ...details });
    this.name = 'GraphDuplicateError';
  }
}

// ============================================
// ERROR CREATION HELPERS
// ============================================

/**
 * Creates and throws a permission error
 * @throws {GraphPermissionError}
 */
export function createPermissionError(entityType: 'node' | 'edge', type: string, details?: Record<string, unknown>): never {
  throw new GraphPermissionError(`create ${entityType}`, entityType, type, details);
}

/**
 * Creates and throws an edge validation error
 * @throws {GraphEdgeValidationError}
 */
export function createEdgeValidationError(edgeType: string, nodeType: string, nodeRole: 'source' | 'target', details?: Record<string, unknown>): never {
  throw new GraphEdgeValidationError(edgeType, nodeType, nodeRole, details);
}

// ============================================
// ERROR HANDLING HELPERS
// ============================================

/**
 * Handles Supabase/PostgreSQL errors with standardized error messages
 * @throws {GraphError}
 */
export function handleSupabaseError(
  error: PostgrestError,
  context: {
    operation?: string;
    entityType?: string;
    nodeType?: string;
    edgeType?: string;
    [key: string]: unknown;
  }
): never {
  // Handle duplicate key errors (23505)
  if (error.code === '23505') {
    const entityType = context.entityType || 'entity';
    const type = context.nodeType || context.edgeType || 'unknown';
    throw new GraphDuplicateError(entityType, type, { originalError: error, ...context });
  }

  // Handle foreign key constraint violations (23503)
  if (error.code === '23503') {
    throw new GraphNotFoundError(context.entityType || 'entity', 'referenced record', { originalError: error, ...context });
  }

  // Handle check constraint violations (23514) - includes module permission checks
  if (error.code === '23514' || error.message?.includes('check_module_enabled')) {
    const type = context.nodeType || context.edgeType || 'unknown';
    throw new GraphPermissionError(context.operation || 'operation', context.entityType || 'entity', type, { originalError: error, ...context });
  }

  // Handle not found errors (PGRST116)
  if (error.code === 'PGRST116') {
    throw new GraphNotFoundError(context.entityType || 'entity', 'record', { originalError: error, ...context });
  }

  // Generic database error
  throw new GraphError(`Database error: ${error.message}`, GraphErrorCode.DATABASE_ERROR, { originalError: error, ...context });
}

/**
 * Handles validation errors with standardized error messages
 * @throws {GraphValidationError}
 */
export function handleValidationError(
  errors: unknown[],
  context: {
    nodeType?: string;
    edgeType?: string;
    [key: string]: unknown;
  }
): never {
  const entityType = context.nodeType || context.edgeType || 'entity';
  throw new GraphValidationError(`Validation failed for ${entityType}`, errors, context);
}

// ============================================
// ERROR TYPE GUARDS
// ============================================

/**
 * Check if an error is a GraphError
 */
export function isGraphError(error: unknown): error is GraphError {
  return error instanceof GraphError;
}

/**
 * Check if an error is a specific type of GraphError
 */
export function isGraphErrorCode(error: unknown, code: GraphErrorCode): boolean {
  return isGraphError(error) && error.code === code;
}

/**
 * Get a user-friendly error message from any error
 */
export function getErrorMessage(error: unknown): string {
  if (isGraphError(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

/**
 * Get error details for debugging
 */
export function getErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (isGraphError(error)) {
    return error.details;
  }

  return undefined;
}
